import { useEffect, useState } from 'react';
import { apiClient } from '@kss/api';

/**
 * Auth-aware `<img>` replacement.
 *
 * Why this exists: backend's `/api/Image/<sha>.png` endpoint serves the
 * uploaded image but requires a Bearer header. Plain `<img src=…>` can't
 * attach Authorization, so it just renders broken. This component
 * fetches the image via authed XHR, converts the response to a blob
 * URL, and uses THAT as the src — works with the existing token.
 *
 * Pass-throughs:
 *   - `data:` URLs    → rendered directly (in-memory preview before save)
 *   - `https://…` URL → rendered directly (already-public image)
 *   - `/api/Image/…`  → fetched with auth → blob URL
 *   - `/uploads/…`    → fetched with auth → blob URL (legacy path)
 *
 * Cleans up the blob URL on unmount.
 *
 * When backend G61 lands and `/api/Image/<sha>` becomes anonymous, this
 * component is a no-op (the direct `<img>` would work too) — but the
 * code path is unchanged, so we keep it for defense-in-depth.
 */
export interface AuthImgProps {
  src: string;
  alt?: string;
  className?: string;
  onError?: () => void;
  /** Optional inline style — same shape as `<img style>` */
  style?: React.CSSProperties;
}

/**
 * Quick check for sources that the browser can render directly without
 * an authed fetch — data URLs, already-absolute http(s), and blob URLs.
 */
function isPassThrough(src: string): boolean {
  return !!src && (
    src.startsWith('data:') ||
    src.startsWith('blob:') ||
    /^https?:\/\//i.test(src)
  );
}

export function AuthImg({ src, alt = '', className, onError, style }: AuthImgProps) {
  // For pass-through URLs (data:, blob:, https://) render synchronously —
  // no useEffect dance, no flash of empty placeholder. This is the common
  // case for the upload widget's in-memory preview right after the user
  // picks a file.
  const passThrough = isPassThrough(src);
  const [authedBlobUrl, setAuthedBlobUrl] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (passThrough || !src) {
      setAuthedBlobUrl(null);
      setErrored(false);
      return;
    }
    let cancelled = false;
    let revokeUrl: string | null = null;
    setErrored(false);
    void (async () => {
      try {
        const stripped = src.replace(/^\/api\//, '/');
        const r = await apiClient.get(stripped, { responseType: 'blob' });
        if (cancelled) return;
        const blob = r.data as Blob;
        const obj = URL.createObjectURL(blob);
        revokeUrl = obj;
        setAuthedBlobUrl(obj);
      } catch {
        if (cancelled) return;
        setErrored(true);
        onError?.();
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [src, passThrough, onError]);

  const finalSrc = passThrough ? src : authedBlobUrl;

  if (errored || !finalSrc) {
    return (
      <div
        className={className}
        style={style}
        role="img"
        aria-label={alt}
      />
    );
  }
  return (
    <img
      src={finalSrc}
      alt={alt}
      className={className}
      style={style}
      onError={() => { setErrored(true); onError?.(); }}
    />
  );
}
